import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { m as messageId } from './form.js';
import { f as danger, g as enlarge } from './index2.js';
import { d as defineCustomElement$2 } from './pds-icon2.js';

const pdsSelectTokensCss = ":host{--pine-select-color-background-danger:var(--pine-color-red-050)}";

const labelCss = ":host{display:inline-block}:host([aria-disabled=true]) label{color:var(--pine-color-text-label-disabled)}:host([aria-readonly=true]) label{color:var(--pine-color-text-label-readonly)}label{color:var(--pine-color-text-label);font:var(--pine-typography-body-medium);letter-spacing:var(--pine-letter-spacing)}";

const pdsSelectCss = ":host .hidden,:host :host([hidden]){display:none}.pds-select{display:grid;-ms-flex-direction:column;flex-direction:column;grid-template-areas:\"label label\" \"field field\" \"message message\";grid-template-columns:1fr minmax(2rem, -webkit-min-content);grid-template-columns:1fr minmax(2rem, min-content);grid-template-rows:-webkit-min-content -webkit-min-content -webkit-min-content;grid-template-rows:min-content min-content min-content;position:relative;width:100%}label{grid-area:label;-webkit-margin-after:var(--pine-dimension-2xs);margin-block-end:var(--pine-dimension-2xs)}select{-webkit-appearance:none;-moz-appearance:none;appearance:none;background:var(--pine-color-background-container);border:var(--pine-border);border-radius:var(--pine-dimension-125);font:var(--pine-typography-body);grid-area:field;letter-spacing:var(--pine-letter-spacing);padding:var(--pine-dimension-xs) var(--pine-dimension-150);padding-right:var(--pine-dimension-450);position:relative}select:hover{border-color:var(--pine-color-border-hover);cursor:pointer}select:focus-visible{border-color:var(--pine-color-border-active);outline:var(--pine-outline-focus);outline-offset:var(--pine-border-width)}select:disabled{background-color:var(--pine-color-background-container-disabled);border-color:var(--pine-color-border-disabled);color:var(--pine-color-text-disabled);cursor:not-allowed}select:has(~.pds-select__message .pds-select__error-message){background-color:var(--pine-select-color-background-danger);border-color:var(--pine-color-border-danger)}select:has(~.pds-select__message .pds-select__error-message):focus-visible{outline-color:var(--pine-color-focus-ring-danger)}:host(.is-invalid) select{background-color:var(--pine-select-color-background-danger);border-color:var(--pine-color-border-danger)}:host(.is-invalid) select:focus-visible{outline-color:var(--pine-color-focus-ring-danger)}.pds-select__error-message,.pds-select__helper-message{font:var(--pine-typography-body-sm-medium);-webkit-margin-after:0;margin-block-end:0;-webkit-margin-before:var(--pine-dimension-2xs);margin-block-start:var(--pine-dimension-2xs)}.pds-select__error-message{-ms-flex-align:center;align-items:center;color:var(--pine-color-text-message-danger);display:-ms-flexbox;display:flex;gap:var(--pine-dimension-2xs)}.pds-select__message{grid-area:message}.pds-select__select-icon{-ms-flex-align:center;align-items:center;display:-ms-flexbox;display:flex;grid-column:-1/-2;grid-row:2/3;height:2.5rem;pointer-events:none;position:relative;z-index:1}";

const PdsSelect$1 = /*@__PURE__*/ proxyCustomElement(class PdsSelect extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.pdsSelectChange = createEvent(this, "pdsSelectChange", 7);
        /**
         * Indicates whether or not the select field is disabled.
         * @defaultValue false
         */
        this.disabled = false;
        /**
         * Indicates whether multiple options can be selected.
         * @defaultValue false
         */
        this.multiple = false;
        /**
         * Indicates whether or not the select field is required.
         * @defaultValue false
         */
        this.required = false;
        /**
         * Emits an event on input change.
        */
        this.onSelectUpdate = (e) => {
            const target = e.target;
            const values = Array.from(target.options)
                .filter((option) => (option.selected))
                .map((option) => (option.value));
            if (values.length === 1 && !this.multiple) {
                this.value = values[0];
            }
            else {
                this.value = values;
            }
            this.pdsSelectChange.emit(e);
        };
        /**
         * Handles the change event for the slot element.
         * This method is triggered when the slot content changes.
         * It updates the inner HTML of the select element by cloning and appending
         * the assigned <option> elements from the slot.
         */
        this.handleSlotChange = () => {
            const slot = this.slotContainer.querySelector('slot');
            this.selectEl.innerHTML = '';
            const assignedElements = slot.assignedElements({ flatten: true });
            assignedElements.forEach((item) => {
                if (['OPTION', 'OPTGROUP'].includes(item.tagName)) {
                    const clonedItem = item.cloneNode(true);
                    if (clonedItem.tagName === 'OPTION' && clonedItem.value === this.value) {
                        clonedItem.selected = true;
                    }
                    this.selectEl.appendChild(clonedItem);
                }
            });
            this.updateSelectedOption();
        };
    }
    /**
     * Handles the change in the value of the select component.
     * This method is called whenever the value of the select component changes.
     * It updates the selected option accordingly.
     */
    valueChanged() {
        this.updateSelectedOption();
    }
    componentWillLoad() {
        this.updateSelectedOption();
    }
    /**
     * Updates the selected option in the select element based on the current value.
     *
     * This method iterates through all the options of the select element and sets the
     * 'selected' attribute on the option that matches the current value. If an option
     * does not match the current value, the 'selected' attribute is removed.
     *
     * @private
     * @returns {void}
     */
    updateSelectedOption() {
        if (this.selectEl) {
            const options = this.selectEl.options;
            // Update the selected attribute for all options.
            Array.from(options).map((option) => {
                if (Array.isArray(this.value)) {
                    option.selected = this.value.includes(option.value);
                }
                else {
                    option.selected = this.value === option.value;
                }
            });
        }
    }
    getHelperMessage() {
        return this.helperMessage && (h("p", { class: "pds-select__helper-message", id: messageId(this.componentId, 'helper') }, this.helperMessage));
    }
    getErrorMessage() {
        return this.errorMessage && (h("p", { class: "pds-select__error-message", id: messageId(this.componentId, 'error'), "aria-live": "assertive" }, h("pds-icon", { icon: danger, size: "small" }), this.errorMessage));
    }
    renderMessages() {
        if (!this.helperMessage && !this.errorMessage)
            return null;
        return (h("div", { class: "pds-select__message" }, this.getHelperMessage(), this.getErrorMessage()));
    }
    classNames() {
        const classNames = [];
        if (this.invalid) {
            classNames.push('is-invalid');
        }
        if (this.disabled) {
            classNames.push('is-disabled');
        }
        return classNames.join('  ');
    }
    render() {
        return (h(Host, { key: '6eea9a5492e4b7e415922bce0aa8f2295792a9e4', "aria-disabled": this.disabled ? 'true' : null, class: this.classNames() }, h("div", { key: '3180e1eff494f846f395d9db6730926d86d1e54a', class: "pds-select" }, this.label &&
            h("label", { key: '90dc521ef10b81a9f58a0f0a2cfa4bc685312119', htmlFor: this.componentId }, this.label), h("select", { key: 'd6576d25601de6c41893568927b64e530bf9f266', autocomplete: this.autocomplete || undefined, class: "pds-select__field", disabled: this.disabled, id: this.componentId, multiple: this.multiple, name: this.name, onChange: this.onSelectUpdate, required: this.required, ref: (el) => (this.selectEl = el) }), h("div", { key: '87c53bb7bf315e170f60e5b58afcbf623d1aa65c', "aria-hidden": "true", class: "hidden", ref: (el) => (this.slotContainer = el) }, h("slot", { key: '8cafbc8cc8e23de0ab69b9d6b7d23c896df530b6', onSlotchange: this.handleSlotChange })), this.renderMessages(), !this.multiple && h("pds-icon", { key: '48758d321edbd38ff6bd31ddd1902627202e196e', class: "pds-select__select-icon", icon: enlarge }))));
    }
    static get watchers() { return {
        "value": ["valueChanged"]
    }; }
    static get style() { return pdsSelectTokensCss + (labelCss + pdsSelectCss); }
}, [1, "pds-select", {
        "autocomplete": [1],
        "componentId": [1, "component-id"],
        "disabled": [4],
        "errorMessage": [1, "error-message"],
        "helperMessage": [1, "helper-message"],
        "invalid": [4],
        "label": [1],
        "multiple": [4],
        "name": [1],
        "required": [4],
        "value": [1025]
    }, undefined, {
        "value": ["valueChanged"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["pds-select", "pds-icon"];
    components.forEach(tagName => { switch (tagName) {
        case "pds-select":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, PdsSelect$1);
            }
            break;
        case "pds-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const PdsSelect = PdsSelect$1;
const defineCustomElement = defineCustomElement$1;

export { PdsSelect, defineCustomElement };
//# sourceMappingURL=pds-select.js.map

//# sourceMappingURL=pds-select.js.map